import type { ReactNode } from 'react';
import { useLiffWithMember } from './useLiffWithMember';
import { LiffLoginGate } from '../components/LiffLoginGate';
import { Loading } from '../components/Loading';

/**
 * 取得登入後導回目前頁的 redirect URI
 */
function getLoginRedirectUri(): string {
  return (
    window.location.origin + window.location.pathname + window.location.search
  );
}

/**
 * LIFF 門檻 Hook：封裝 useLiffWithMember + LiffLoginGate + loading 狀態。
 * 回傳 renderGate(children)，在 !ready 或 loading 時顯示 Loading，有 error 時由 LiffLoginGate 顯示錯誤；否則渲染 children。
 * 適用於僅需「登入即加會員」的 App（Preorder、OnlineShop、Survey、Pickup 等）；會員中心等需額外 useUserDetail 的 App 可維持原本寫法。
 */
export function useRequireLiffMember() {
  const {
    isReady,
    profile,
    isLoggedIn,
    shouldUseLiff,
    error: liffError,
    ensureError,
    isMemberLoading,
    liff,
  } = useLiffWithMember();

  const ready = isReady;
  const loading = isMemberLoading;
  const error = (() => {
    if (liffError) {
      return liffError instanceof Error
        ? liffError
        : new Error(String(liffError));
    }
    if (ensureError) {
      return ensureError instanceof Error
        ? ensureError
        : new Error(String(ensureError));
    }
    return null;
  })();

  const renderGate = (children: ReactNode) => (
    <LiffLoginGate
      isReady={ready}
      shouldUseLiff={shouldUseLiff}
      isLoggedIn={isLoggedIn}
      error={error}
      onLoginClick={() => liff?.login?.({ redirectUri: getLoginRedirectUri() })}
    >
      {!ready || loading ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        children
      )}
    </LiffLoginGate>
  );

  return {
    ready,
    isLoggedIn,
    error,
    profile,
    renderGate,
  };
}
